import type { Disposable } from "@/shared/contracts/lifecycle";
import type {
  HoverPointerKind,
  HoverPointerPosition,
  HoverRectLike,
  HoverPointerSnapshot,
} from "./hoverPointerTypes";

type Listener = (snapshot: HoverPointerSnapshot) => void;
type HoverAnchorRectResolver = (targetId: string) => HoverRectLike | null;

interface SetVirtualTouchPositionOptions {
  readonly hoverAnchorTargetId?: string | null;
}

interface ClearVirtualTouchPositionOptions {
  readonly preserveHoverAnchor?: boolean;
}

export class HoverPointerModel implements Disposable {
  private mousePosition: HoverPointerPosition | null = null;
  private virtualTouchPosition: HoverPointerPosition | null = null;
  private hoverAnchorTargetId: string | null = null;
  private activeKind: HoverPointerKind | null = null;
  private readonly listeners = new Set<Listener>();

  setMousePosition(position: HoverPointerPosition): void {
    this.mousePosition = { x: position.x, y: position.y };
    this.activeKind = "mouse";
    this.notify();
  }

  setVirtualTouchPosition(
    position: HoverPointerPosition,
    options: SetVirtualTouchPositionOptions = {},
  ): void {
    this.virtualTouchPosition = { x: position.x, y: position.y };
    this.hoverAnchorTargetId = options.hoverAnchorTargetId ?? null;
    this.activeKind = "virtualTouch";
    this.notify();
  }

  setHoverAnchorTargetId(targetId: string | null): void {
    if (this.hoverAnchorTargetId === targetId) {
      return;
    }
    this.hoverAnchorTargetId = targetId;
    this.notify();
  }

  clearVirtualTouchPosition(
    options: ClearVirtualTouchPositionOptions = {},
  ): void {
    if (
      this.virtualTouchPosition === null &&
      this.activeKind !== "virtualTouch" &&
      (options.preserveHoverAnchor || this.hoverAnchorTargetId === null)
    ) {
      return;
    }
    this.virtualTouchPosition = null;
    if (!options.preserveHoverAnchor) {
      this.hoverAnchorTargetId = null;
    }
    if (this.activeKind === "virtualTouch") {
      this.activeKind = null;
    }
    this.notify();
  }

  restoreVirtualTouchAnchorPosition(
    resolveRect: HoverAnchorRectResolver,
  ): boolean {
    const targetId = this.hoverAnchorTargetId;
    if (targetId === null) {
      return false;
    }
    const rect = resolveRect(targetId);
    if (rect === null) {
      this.clearVirtualTouchPosition();
      return false;
    }
    this.virtualTouchPosition = {
      x: (rect.left + rect.right) / 2,
      y: (rect.top + rect.bottom) / 2,
    };
    this.activeKind = "virtualTouch";
    this.notify();
    return true;
  }

  getSnapshot(): HoverPointerSnapshot {
    const position =
      this.activeKind === "mouse"
        ? this.mousePosition
        : this.activeKind === "virtualTouch"
          ? this.virtualTouchPosition
          : null;
    return {
      kind: this.activeKind,
      position,
      hoverAnchorTargetId: this.hoverAnchorTargetId,
    };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Re-broadcast the current snapshot without a state change. Use when an
  // external condition (e.g., an `inert` overlay being removed) means existing
  // subscribers should re-evaluate against the unchanged pointer position.
  republish(): void {
    this.notify();
  }

  dispose(): void {
    this.listeners.clear();
  }

  private notify(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}
